<section class="sav-contact-hero" aria-label="Contact SavingArena">
  <div class="sav-site-container">
    <app-breadcrumb [items]="crumbs" />
    <div class="sav-contact-hero-inner">
      <span class="sav-contact-eyebrow">Contact us</span>
      <h1 class="sav-contact-title">{{ h1() }}</h1>
      <p class="sav-contact-subtitle">{{ shortDescription() }}</p>
    </div>
  </div>
</section>

<section class="sav-contact-body" aria-label="Contact details and enquiry form">
  <div class="sav-site-container">
    <div class="sav-contact-grid">
      <aside class="sav-contact-channels" aria-label="Ways to reach us">
        @for (channel of channels(); track channel.label + $index) {
          <article class="sav-contact-channel-card">
            <span class="sav-contact-channel-icon" aria-hidden="true">
              @switch (channel.icon) {
                @case ('mail') {
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <rect x="3" y="5" width="18" height="14" rx="2"></rect>
                    <path d="m3 7 9 6 9-6"></path>
                  </svg>
                }
                @case ('phone') {
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path
                      d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.68 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.32 1.85.55 2.81.68A2 2 0 0 1 22 16.92z"
                    ></path>
                  </svg>
                }
                @default {
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                }
              }
            </span>
            <div class="sav-contact-channel-body">
              @if (channel.label) {
                <span class="sav-contact-channel-label">{{ channel.label }}</span>
              }
              @if (channel.value) {
                @if (channel.href) {
                  <a class="sav-contact-channel-value" [href]="channel.href">{{ channel.value }}</a>
                } @else {
                  <span class="sav-contact-channel-value">{{ channel.value }}</span>
                }
              }
              @if (channel.description) {
                <p class="sav-contact-channel-desc">{{ channel.description }}</p>
              }
            </div>
          </article>
        }
      </aside>

      <div class="sav-contact-form-card">
        <div class="sav-contact-form-head">
          <h2>{{ formHeading() }}</h2>
          <p>{{ formDescription() }}</p>
        </div>

        @if (submitted()) {
          <div class="sav-contact-success" role="status">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
            <p>{{ successMessage() }}</p>
          </div>
        }

        @if (sendError()) {
          <!-- The form below keeps everything the visitor typed, so "try again" costs them nothing. -->
          <div class="sav-contact-error-banner" role="alert">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9"></circle>
              <path d="M12 8v5"></path>
              <path d="M12 16.5h.01"></path>
            </svg>
            <p>{{ sendError() }}</p>
          </div>
        }

        <form
          [formGroup]="contactForm"
          (ngSubmit)="submitForm()"
          class="sav-contact-form"
          novalidate
        >
          <div class="sav-contact-form-row">
            <label class="sav-contact-field">
              <span class="sav-contact-field-label">Name</span>
              <input
                formControlName="name"
                type="text"
                autocomplete="name"
                placeholder="Your full name"
              />
              @if (contactForm.controls.name.touched && contactForm.controls.name.invalid) {
                <small class="sav-contact-error">Please enter your name.</small>
              }
            </label>

            <label class="sav-contact-field">
              <span class="sav-contact-field-label">Email</span>
              <input
                formControlName="email"
                type="email"
                autocomplete="email"
                placeholder="you@example.com"
              />
              @if (contactForm.controls.email.touched && contactForm.controls.email.invalid) {
                <small class="sav-contact-error">Enter a valid email address.</small>
              }
            </label>
          </div>

          <label class="sav-contact-field">
            <span class="sav-contact-field-label"
              >Subject <span class="sav-contact-field-optional">(optional)</span></span
            >
            <input formControlName="subject" type="text" placeholder="What is this about?" />
          </label>

          <label class="sav-contact-field">
            <span class="sav-contact-field-label">Message</span>
            <textarea
              formControlName="message"
              rows="6"
              placeholder="Tell us how we can help…"
            ></textarea>
            @if (contactForm.controls.message.touched && contactForm.controls.message.invalid) {
              <small class="sav-contact-error"
                >Please add at least 20 characters so we can help.</small
              >
            }
          </label>

          <!--
            Honeypot. Hidden from sight AND from assistive tech (`aria-hidden` + `tabindex="-1"`),
            so nobody can be asked to fill it by accident; a bot that fills every field will.
            `autocomplete="off"` stops a browser helpfully filling it with a saved value.
          -->
          <div class="sav-contact-honeypot" aria-hidden="true">
            <label>
              Company website
              <input
                formControlName="companyWebsite"
                type="text"
                tabindex="-1"
                autocomplete="off"
              />
            </label>
          </div>

          <button type="submit" class="sav-contact-submit" [disabled]="isSending()">
            {{ isSending() ? 'Sending…' : 'Send Message' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</section>

<app-big-sale-banner />
